import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import Select from 'react-select';
import {
  Text,
  Spacer,
  ButtonPublish,
  LoadingDot,
  Button,
  Icon,
  ButtonTemporaryPublish,
  Title
} from 'components/atoms';
import {
  useEditorContext, actions, status
} from 'context/EditorContext';
import { useRouterContext } from 'hooks';
import { toast } from 'react-toastify';
import { Toast, PageMenuSelect } from 'containers';
import { screensOpts } from 'components/molecules/ResponsiveViewer/ResponsiveViewer';
import {
  ResponsiveViewerSelect, Tooltip, ModalConfirm, Modal
} from 'components/molecules';
import { publishContent } from 'services/requests'
import { intlType } from 'types'
import { generateEditorPath, computeDateDistance } from 'services/generators';
import { useDispatch, useSelector } from 'react-redux'
import { deleteDraft } from 'redux/draftsSlice'
import { injectIntl, FormattedMessage } from 'react-intl'
import Status from 'components/atoms/Status/Status'
import { PORTAL_WWW, rootSubwebs } from 'services/constants';
import { Link } from 'react-router-dom'

const options = [
  {
    label: 'Edição em PT',
    value: 'pt',
  },
  {
    label: 'Edição em EN',
    value: 'en',
  },
];

const PageMenuMain = ({ loading, intl }) => {
  /* eslint-disable-next-line no-unused-vars */
  const [
    {
      pageLiveData,
      pageEdited,
      pageStatus,
      pageTitle,
      screen,
      pageSlug,
      pageTempSlug
    },
    dispatch,
  ] = useEditorContext();
  const dispatchRedux = useDispatch()
  const { pages: drafts } = useSelector(
    (state) => state.drafts
  )

  const {
    history,
    match: { params },
  } = useRouterContext();

  const [publishLoading, setPublishLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(
    {
      isOpen: false,
      date: null,
      user: null
    }
  )

  const [confirmationLanguageLeave, setConfirmationLanguageLeave] = useState(
    {
      isOpen: false,
      hasConfirmed: false,
      nextLang: null,
    }
  )

  const [confirmRollback, setConfirmRollback] = useState(
    {
      isOpen: false,
      hasConfirmed: false,
    }
  )

  const [confirmPreview, setConfirmPreview] = useState(
    {
      isOpen: false,
    }
  )

  const pageDraft = useMemo(() => {
    const { pageId } = params;
    return drafts[pageId]
  }, [params, drafts])

  const hasEdited = useMemo(() => {
    if (pageEdited) return true
    if (pageDraft) return true
    if (pageStatus === status.edited || pageStatus === status.local) return true
    return false
  }, [pageDraft, pageEdited, pageStatus])

  const changeLanguage = (nextLang) => {
    dispatchRedux(deleteDraft(params.pageId))

    // eslint-disable-next-line no-unused-vars
    const { moduleId, ...paramsWithoutModule } = params
    // moduleId is removed because the pages don't necessarely have the same modules
    const newPathFromLang = generateEditorPath({ ...paramsWithoutModule, lang: nextLang })
    history.push(newPathFromLang);
  }

  const resetConfirmationLanguageLeave = () => {
    setConfirmationLanguageLeave(
      {
        isOpen: false,
        hasConfirmed: false,
        nextLang: null
      }
    )
  }

  const handleChange = ({ value: newLang }) => {
    // only open confirmation model
    // when going to different language and has a local draft
    if (newLang !== params.lang && pageStatus === status.local) {
      setConfirmationLanguageLeave(
        {
          isOpen: true,
          hasConfirmed: false,
          nextLang: newLang,
        }
      )
    } else changeLanguage(newLang)
  };

  const handleConfirmLanguageLeave = (shouldChangeLanguage) => {
    const { nextLang } = confirmationLanguageLeave
    if (shouldChangeLanguage && nextLang) changeLanguage(nextLang)
    else resetConfirmationLanguageLeave()
  }

  // reset confirmationLanguageLeave on language change
  useEffect(() => {
    resetConfirmationLanguageLeave()
  }, [params.lang])

  const activeValue = useMemo(() => {
    const { lang } = params;
    const activeValue = options.find(values => values.value === lang);
    return activeValue;
  }, [params]);

  // disable buttons when loading
  const shouldDisableButtons = useMemo(() => loading, [loading]);

  // States:
  // None = 0,
  // Draft = 1,
  // Published = 2,
  // Archive = 3,
  // Deleted = 4
  const onPublish = async ({ hardSave } = { hardSave: false }) => {
    setPublishLoading(true);
    const { pageId, lang, subweb } = params;
    const isSaved = pageStatus === status.edited
    const isDraft = pageStatus === status.draft
    const isArchive = pageStatus === status.archive
    const isPrePublished = pageStatus === status.prePublished

    if (hasEdited || isSaved || isDraft || isArchive || isPrePublished) {

      // when already saved or pre-published then publish, else save
      const state = isSaved || isPrePublished ? 2 : 1

      try {
        const { data: { pageContent: newPage } } = await publishContent({
          subweb,
          pageId,
          language: lang,
          pageData: pageLiveData,
          state,
          hardSave,
        })

        dispatch({
          type: isSaved || isPrePublished ? actions.PAGE_DATA_PUBLISH : actions.PAGE_DATA_SAVE,
          data: newPage,
        });

        // delete local storage draft
        dispatchRedux(deleteDraft(pageId))
        const toastTitle = isSaved || isPrePublished ? 'Página publicada' : 'Página gravada'
        const toastDescription = isSaved || isPrePublished ? 'As alterações foram publicadas!' : 'Para publicar clique em publicar'
        toast.success(
          <Toast
            type="success"
            title={toastTitle}
            description={toastDescription}
          />
        );
        setPublishLoading(false);
        return;
      } catch (err) {
        // handle conflict, ask if user really wants to publish
        if (err.response && err.response.status === 409) {
          const { data } = err.response
          setConfirmationModal({
            isOpen: true,
            date: data.date,
            user: data.user
          })
        } else {
          toast.error(
            <Toast
              type="error"
              title="Erro a publicar página."
              description={err.message}
            />
          );
        }
        return;
      }
    }
    toast.info(
      <Toast
        type="info"
        title="Página sem alterações"
        description="Para publicar deve fazer alterações primeiro"
      />
    );
    setPublishLoading(false);
  };

  const onPublishPrevisualization = async ({ hardSave } = { hardSave: false }) => {
    setPublishLoading(true);
    const { pageId, lang, subweb } = params;
    const isEdited = pageStatus === status.edited
    const isArchive = pageStatus === status.archive
    const isPrePublished = pageStatus === status.prePublished

    if (isEdited || isArchive || isPrePublished) {
      if (!isPrePublished) {
        try {
          const { data } = await publishContent({
            subweb,
            pageId,
            language: lang,
            pageData: pageLiveData,
            state: 2, // it means its publish, check if there will be a new pre publish state
            hardSave,
            preview: true // preview
          })

          dispatch({
            type: actions.PAGE_DATA_PRE_PUBLISH,
            data,
          });

          toast.success(
            <Toast
              type="success"
              title="Pré visualização criada"
              description="Foi criado um link temporário para pré-visualização"
            />
          );

          setConfirmPreview({ isOpen: true })
          setPublishLoading(false);
          return;
        } catch (err) {
          setPublishLoading(false);
          toast.error(
            <Toast
              type="error"
              title="Erro a criar pré-visualização"
              description={err.message}
            />
          )
        }
      }
      else {
        setConfirmPreview({ isOpen: true })
        setPublishLoading(false);
      }
      return;
    }

    // if trying to publish but the changes are not save
    toast.info(
      <Toast
        type="info"
        title="Página não gravada"
        description="Para pré-visualizar deve gravar as alterações primeiro"
      />
    );
    setPublishLoading(false);
  };

  const screenValue = useMemo(
    () => screensOpts.find(({ value }) => screen === value),
    [screen]
  );

  const handleChangeScreen = ({ value: newScreen }) => {
    dispatch({
      type: actions.SCREEN_SET,
      data: newScreen,
    });
  };

  const handleConfirm = async (hasConfirmed) => {
    if (hasConfirmed) {
      onPublish({ hardSave: true })
    } else {
      dispatchRedux(deleteDraft(params.pageId))
      dispatch({
        type: actions.PAGE_REFETCH,
      });
    }
    setConfirmationModal({
      ...confirmationModal,
      isOpen: false
    })
  }

  /* rollback */
  const discardChanges = async () => {
    // eslint-disable-next-line no-unused-vars
    const { moduleId, ...paramsWithoutModule } = params
    // moduleId is removed because the pages don't necessarely have the same modules
    const pagePath = generateEditorPath({ ...paramsWithoutModule })
    history.push(pagePath);
    dispatchRedux(deleteDraft(params.pageId))
    dispatch({
      type: actions.PAGE_REFETCH,
    });
  }

  const handleConfirmRollback = (hasConfirmed) => {
    if (hasConfirmed) {
      discardChanges()
    }
    setConfirmRollback({
      isOpen: false,
      hasConfirmed: false
    })
  }

  const handleRollback = () => {
    setConfirmRollback({
      isOpen: true,
      hasConfirmed: false
    })
  }
  /* /rollback */

  const pageUrl = useMemo(() => {
    const { lang, subweb, pageId } = params;
    const subwebPath = rootSubwebs.includes(subweb) ? '' : `/${subweb}`
    const slug = pageSlug || `page/${pageId}`
    const pageUrl = `${PORTAL_WWW}/${lang}${subwebPath}/${slug}`
    return pageUrl
  }, [params, pageSlug])

  const pageTempUrl = useMemo(() => {
    const { lang, subweb } = params;
    
    const subwebPath = `/${subweb}`
    const slug = pageTempSlug
    const pageUrl = `${PORTAL_WWW}/${lang}${subwebPath}/${slug}`
    return pageUrl
  }, [params, pageTempSlug])

  const lastModified = useMemo(() => {
    if (pageStatus === status.local && pageDraft) return pageDraft.editedAt
    const SEO = pageLiveData ? pageLiveData.find(module => module.name === 'SEO') : null
    if (SEO && SEO.lastModified) return SEO.lastModified
    return null
  }, [pageLiveData, pageDraft, pageStatus])

  const statusTooltipMessage = useMemo(() => {
    let message = intl.formatMessage({ id: `page.state.${pageStatus}` })
    const editedDateDistance = computeDateDistance(lastModified)
    if (pageStatus === status.prePublished) message = ''
    if (editedDateDistance) message = `${message} há ${editedDateDistance}`
    return message
    // eslint-disable-next-line
  }, [pageStatus, lastModified])

  const editorPath = useMemo(() => {
    const { subweb, lang, pageId } = params
    return generateEditorPath({ subweb, lang, pageId })
  }, [params])

  const conflictMessage = useMemo(() => {
    const { user } = confirmationModal
    if (user) return `Esta página foi editada pelo editor(a) ${user}, pretende publicar na mesma?`
    return 'Esta página foi editada por outra pessoa. Deseja guardar as suas alterações?'
  }, [confirmationModal])

  const renderPopups = () => (
    <>
      <ModalConfirm
        isOpen={confirmationLanguageLeave.isOpen}
        toggle={() => setConfirmationLanguageLeave({
          ...confirmationLanguageLeave,
          isOpen: !confirmationLanguageLeave.isOpen
        })}
        onChange={handleConfirmLanguageLeave}
        title="Tem alterações por gravar"
        message="Pretende mudar de língua na mesma?"
        textConfirm="Mudar"
        textDiscard="Cancelar"
      />
      <ModalConfirm
        isOpen={confirmationModal.isOpen}
        toggle={() => setConfirmationModal({
          ...confirmationModal,
          isOpen: !confirmationModal.isOpen
        })}
        onChange={handleConfirm}
        title="Conflito"
        message={conflictMessage}
        textConfirm="Guardar"
        textDiscard="Cancelar"
      />
      <ModalConfirm
        isOpen={confirmRollback.isOpen}
        toggle={() => setConfirmRollback({
          ...confirmRollback,
          isOpen: !confirmRollback.isOpen
        })}
        onChange={handleConfirmRollback}
        title="Descartar edições"
        message="Vai perder as suas novas edições, pretende continuar?"
        textConfirm="Continuar"
        textDiscard="Cancelar"
      />
      <Modal
        isOpen={confirmPreview.isOpen}
        toggle={() => setConfirmPreview({
          ...confirmPreview,
          isOpen: !confirmPreview.isOpen
        })}
      >
        <Title>Link de pré-visualização</Title>
        <ModalCopyLink modifiers={['content']}>
          {pageTempUrl}
          <Tooltip tooltip="Copiar link" placement="bottom">
            <IconButton
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(pageTempUrl)
                toast.success(
                  <Toast
                    type="success"
                    title="Link copiado"
                    description="O link foi copiado para o clipboard."
                  />
                );
              }}
            >
              <Icon
                icon="fal fa-regular fa-copy"
              />
            </IconButton>
          </Tooltip>
        </ModalCopyLink>
        <Text modifiers={['content', 'xs', 'light']}>
          Esta versão da página estará disponível para visualização durante 30 dias.
        </Text>
        <ModalActions>
          <Button
            type="submit"
            modifiers={['marginRight', 'capitalize']}
            onClick={() => setConfirmPreview({
              ...confirmPreview,
              isOpen: !confirmPreview.isOpen
            })}
          >
            Fechar
          </Button>
          <Button
            type="button"
            modifiers={['dark', 'capitalize']}
          >
            <a style={{ textDecoration: 'none', color: 'inherit' }} href={pageTempUrl} target="_blank" rel="noopener noreferrer">Ver página</a>
          </Button>
        </ModalActions>
      </Modal>
    </>
  )

  return (
    <>
      {renderPopups()}
      <PageMenu>
        <PageMenu.Start>
          <Spacer right="s">
            <Select
              classNamePrefix="react-select"
              noOptionsMessage={() => 'Sem opções'}
              options={options}
              name="language"
              value={activeValue}
              onChange={handleChange}
              isDisabled={shouldDisableButtons}
              isSearchable={false}
            />
          </Spacer>
          <PageMenuSelect />
        </PageMenu.Start>
        <PageMenu.Center>
          {loading ? (
            <LoadingDot />
          ) : (
            <PageMenu.Info>
              <PageMenu.Info.Status>
                <Link to={editorPath}>
                  <Status status={pageStatus} />
                  <Spacer right="xs2" />
                  <Text modifiers={['base', 'inline']}>{pageTitle}</Text>
                </Link>
                <Spacer left="xs2" right="xs2">
                  -
                </Spacer>
                <Tooltip tooltip={statusTooltipMessage} placement="bottom">
                  <Text modifiers={['inline']}>
                    <FormattedMessage id={`page.state.${pageStatus}`} />
                  </Text>
                </Tooltip>
              </PageMenu.Info.Status>
              <PageMenu.Info.Action>
                {pageStatus === status.local && (
                  <Tooltip tooltip="descartar alterações" placement="bottom">
                    <Button modifiers={['invisibleDarker', 'small']} onClick={handleRollback}>
                      <Icon icon="fal fa-undo" height={10} />
                    </Button>
                  </Tooltip>
                )}
                {(pageStatus === status.published) && (
                  <Tooltip tooltip="ver página no portal" placement="bottom">
                    <a href={pageUrl} target="_blank" rel="noopener noreferrer">
                      <Button modifiers={['invisibleDarker', 'small']}>
                        <Icon icon="fal fa-external-link" height={10} />
                      </Button>
                    </a>
                  </Tooltip>
                )}
              </PageMenu.Info.Action>
            </PageMenu.Info>
          )}
        </PageMenu.Center>
        <PageMenu.End>
          <Spacer right="xs2">
            <ResponsiveViewerSelect
              options={screensOpts}
              name="screen"
              value={screenValue}
              onChange={handleChangeScreen}
              isDisabled={loading}
            />
          </Spacer>
          {(pageStatus !== status.published) && (
            <ButtonTemporaryPublish
              onClick={onPublishPrevisualization}
              loading={publishLoading}
              pageStatus={pageStatus}
            />
          )}
          <ButtonPublish
            onClick={onPublish}
            loading={publishLoading}
            pageStatus={pageStatus}
          />
        </PageMenu.End>
      </PageMenu>
    </>
  );
};

const PageMenu = styled.div`
  height: 50px;
  width: 100%;
  background-color: ${props => props.theme.colors.bgLighterS};
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xs3}
    ${props => props.theme.spacing.xs};
  align-items: center;

  ${props => props.theme.mixins.select()};
`;

PageMenu.Start = styled.div`
  display: flex;
`;
PageMenu.Center = styled.div`
  display: flex;
  justify-content: center;
`;
PageMenu.End = styled.div`
  display: flex;
  gap: 8px;
`;

PageMenu.Info = styled.div`
  display: flex;
  align-items: center;
`

PageMenu.Info.Status = styled.div`
  display: flex;
  align-items: center;
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

PageMenu.Info.Action = styled.div`
  margin-left: ${props => props.theme.spacing.xs2};
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.xl3};
`;

const ModalCopyLink = styled(Text)`
  padding-top: 12px;
  span {
    margin-left: 0.5em
  }
`

const IconButton = styled.button`
  all: unset;
  cursor: pointer;
  margin-left: 0.5em;
`

PageMenuMain.propTypes = {
  loading: PropTypes.bool,
  intl: intlType.isRequired
};

PageMenuMain.defaultProps = {
  loading: false,
};

export default injectIntl(PageMenuMain);
